import { SDKMessage, SDKResponse } from '@features/sdk-module'
import { PlatformMessage } from '@features/st-pdf-viewer/platform-module'
import { defineModule, defineTask } from '@st/redux'
import {
  BasicUser,
  QuestionnaireTemplateType,
  ScopedFolderMembership,
  STFolderSendState,
  STSDK,
  UpdateFolderAccess,
  FolderAccessRequest
} from '@st/sdk'
import { match } from 'ts-pattern'

type Status = 'loading' | 'loaded' | 'sending' | 'sent'

type Recipient =
  | { type: 'knownUser'; user: BasicUser; email: string }
  | { type: 'unknownUser'; email: string }

export type STSendQuestionnaireContext = {
  sdk: STSDK
}

export type STSendQuestionnaireState = {
  status: Status
  folderId: string
  templateType: QuestionnaireTemplateType

  sendState: STFolderSendState | undefined

  /**
   * State for the TO: field
   */

  editedRecipients: Recipient[]

  inputValue: string
  selectedEmail: string | undefined

  savingFolder: boolean
}

export type STSendQuestionnaireMessage =
  | { type: 'savingFolder' }
  | { type: 'savedFolder' }
  | { type: 'sendingQuestionnaireEmail' }
  | { type: 'resolvedUsers'; users: BasicUser[] }
  | { type: 'sentQuestionnaireEmail' }
  | { type: 'typeEmail'; email: string }
  | { type: 'selectEmail'; email: string | undefined }
  | { type: 'addEmail'; email: string }
  | { type: 'removeEmail'; email: string }
  | { type: 'updatedFolderAccess'; memberships: ScopedFolderMembership[] }
  | { type: 'savedDraft'; message: string }
  | SDKResponse

type STQuestionnaireSendPlatformModule = {
  sdk: SDKMessage
  platform: PlatformMessage
}

type STQuestionSendModuleInit = { folderId: string; templateType: QuestionnaireTemplateType }

export const stSendQuestionnaireModule = defineModule<
  STSendQuestionnaireState,
  STSendQuestionnaireMessage,
  STQuestionSendModuleInit,
  STQuestionnaireSendPlatformModule
>({
  name: 'stSendQuestionnaire',
  init: ({ folderId, templateType }) => {
    return [
      {
        status: 'loading',
        folderId: folderId,
        sendState: undefined,
        editedRecipients: [],
        inputValue: '',
        selectedEmail: undefined,
        templateType: templateType,
        savingFolder: false
      },
      {
        sdk: {
          type: 'request',
          request: {
            type: 'folders/getFolderSendState',
            folderId: folderId,
            templateType: templateType
          }
        }
      }
    ]
  },
  handle: (state, message) => {
    switch (message.type) {
      case 'typeEmail':
        return { ...state, inputValue: message.email }
      case 'addEmail':
        const existingUser = state.sendState?.recipients.find((r) => r.email == message.email)
        let recipient: Recipient = existingUser
          ? { type: 'knownUser', user: existingUser, email: existingUser.email }
          : { type: 'unknownUser', email: message.email }
        return {
          ...state,
          editedRecipients: [...state.editedRecipients, recipient],
          inputValue: '',
          selectedEmail: undefined
        }
      case 'removeEmail':
        return {
          ...state,
          editedRecipients: state.editedRecipients.filter((r) => r.email != message.email),
          selectedEmail: undefined,
          inputValue: ''
        }
      case 'selectEmail':
        return { ...state, selectedEmail: message.email }
      case 'sendingQuestionnaireEmail':
        return { ...state, status: 'sending' }
      case 'sentQuestionnaireEmail':
        return [
          { ...state, status: 'sent' },
          { platform: { type: 'showSnackbar', message: 'Sent email' } }
        ]
      case 'updatedFolderAccess':
        const users = message.memberships.filter((m) => m.scope == 'folder').map((m) => m.user)

        const updatedRecipients: Recipient[] = users.map((user) => {
          return { type: 'knownUser', user: user, email: user.email }
        })

        return {
          ...state,
          sendState: { ...state.sendState!, recipients: users },
          recipients: updatedRecipients
        }
      case 'savedDraft':
        return {
          ...state,
          sendState: { ...state.sendState!, message: message.message }
        }
      case 'savingFolder':
        return { ...state, savingFolder: true }
      case 'savedFolder':
        return { ...state, savingFolder: false }
      case 'response':
        const op = message.operation
        switch (op.type) {
          case 'folders/getFolderSendState':
            return {
              ...state,
              status: match<Status, Status>(state.status)
                .with('sent', () => 'sent')
                .with('loading', () => 'loaded')
                .otherwise(() => 'loaded'),
              sendState: op.response,
              editedRecipients: op.response.recipients.map((user) => {
                return { type: 'knownUser', user: user, email: user.email }
              })
            }
          default:
            return state
        }
      case 'resolvedUsers':
        return {
          ...state,
          editedRecipients: state.editedRecipients.map((recipient) => {
            const user = message.users.find(
              (u) => u.email.toLowerCase() == recipient.email.toLowerCase()
            )

            if (recipient.type == 'unknownUser' && user) {
              return { type: 'knownUser', user: user, email: user.email }
            } else {
              return recipient
            }
          })
        }
      default:
        return state
    }
  }
})

export const sendQuestionnaireEmail = defineTask(
  stSendQuestionnaireModule,
  async (
    { getState, send },
    { message, deleteDraft }: { message: string; deleteDraft?: boolean },
    { sdk }: STSendQuestionnaireContext
  ) => {
    const state = getState()

    send({ type: 'sendingQuestionnaireEmail' })

    send({
      type: 'resolvedUsers',
      users: await resolveUnknownUsers(state.editedRecipients, { sdk })
    })
    const updateRolesRequest = selUpdateFolderAccessRequest(getState())
    if (updateRolesRequest) {
      await sdk.send(updateRolesRequest)
    }

    if (state.sendState?.message != message) {
      await sdk.send({
        type: 'folders/saveFolderDraftMessage',
        folderId: state.folderId,
        templateType: state.templateType,
        message: message
      })
      send({ type: 'savedDraft', message: message })
    }

    await sdk.send({
      type: 'folders/sendClientOrganizerEmail',
      folderId: state.folderId,
      templateType: state.templateType,
      deleteDraft: deleteDraft
    })

    send({ type: 'sentQuestionnaireEmail' })
  }
)

async function resolveUnknownUsers(
  recipients: Recipient[],
  { sdk }: { sdk: STSDK }
): Promise<BasicUser[]> {
  const unknownEmails = recipients.filter((r) => r.type == 'unknownUser').map((r) => r.email)

  if (unknownEmails.length == 0) {
    return []
  }

  const response = await sdk.send({
    type: 'accounts/getOrCreateUsersByEmail',
    emails: unknownEmails
  })

  return recipients.map((r) => {
    if (r.type == 'knownUser') {
      return r.user
    }
    return response.users.find((u) => u.email.toLowerCase() == r.email.toLowerCase())!
  })
}

export function selCanSendQuestionnaire(state: STSendQuestionnaireState) {
  return state.status == 'loaded' && state.editedRecipients.length > 0
}

/**
 * Save the folder, specifically the draft message to send
 * and/or the users who have access to the folder (questionnaire)
 */
export const saveFolder = defineTask(
  stSendQuestionnaireModule,
  async (
    { getState, send },
    { message }: { message: string },
    { sdk }: STSendQuestionnaireContext
  ) => {
    const state = getState()

    send({ type: 'savingFolder' })

    send({
      type: 'resolvedUsers',
      users: await resolveUnknownUsers(state.editedRecipients, { sdk })
    })
    const updateRolesRequest = selUpdateFolderAccessRequest(getState())
    if (updateRolesRequest) {
      await sdk.send(updateRolesRequest)
    }

    if (state.sendState?.message != message) {
      await sdk.send({
        type: 'folders/saveFolderDraftMessage',
        folderId: state.folderId,
        templateType: state.templateType,
        message: message
      })
    }

    send({ type: 'savedFolder' })
  }
)

/**
 * Calculates the necessary API requests to update folder access permissions
 * based on changes made to the recipient list.
 *
 * It compares the original recipients with the `editedRecipients` (uncommitted and stored in the state)
 * to determine which users need their access revoked and which need 'client' access granted.
 *
 * @returns An `UpdateFolderAccess` api request containing a list of `UpdateFolderAccessRequest` objects.
 */
function selUpdateFolderAccessRequest(
  state: STSendQuestionnaireState
): UpdateFolderAccess | undefined {
  // Original recipients who had access.
  const prevUserIds = state.sendState!.recipients.map((r) => r.id)

  // Desired recipients based on edits (only known users can have roles).
  const nextUserIds = state.editedRecipients
    .filter(
      (r): r is { type: 'knownUser'; user: BasicUser; email: string } => r.type === 'knownUser'
    )
    .map((r) => r.user.id)

  // Users in prevUserIds but not in nextUserIds need their access revoked.
  const removeRequests: FolderAccessRequest[] = prevUserIds
    .filter((userId) => !nextUserIds.includes(userId))
    .map((userId) => ({
      type: 'folders/updateFolderAccess',
      provider: 'local',
      uid: userId,
      role: 'revoke'
    }))

  // Users in nextUserIds but not in prevUserIds need client access granted.
  const addRequests: FolderAccessRequest[] = nextUserIds
    .filter((userId) => !prevUserIds.includes(userId))
    .map((userId) => ({
      type: 'folders/updateFolderAccess',
      provider: 'local',
      uid: userId,
      role: 'client'
    }))

  // Combine requests for the final API call payload.
  const request: UpdateFolderAccess = {
    type: 'folders/updateFolderAccess',
    folderId: state.folderId,
    requests: [...removeRequests, ...addRequests]
  }

  return request.requests.length > 0 ? request : undefined
}
